import React from 'react';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Divider from '@mui/material/Divider';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import InputLabel from '@mui/material/InputLabel';
import FormControl from '@mui/material/FormControl';
import Stack from '@mui/material/Stack';
import IconButton from '@mui/material/IconButton';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import AddIcon from '@mui/icons-material/Add';
import { adalApiFetch } from '../../../adalConfig';
import { startOfWeekMon, endOfWeekSun, textfieldDateFormat, timeFormat } from '../../utils/dateFunctions';
import CustomerSelect from '../../components/CustomerSelect';
import SupplierSelect from '../../components/SupplierSelect';
import { getAPIData } from '../../utils/apiFunction';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import dayjs from 'dayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { MobileTimePicker } from '@mui/x-date-pickers/MobileTimePicker';

const MySwal = withReactContent(Swal);

// Placeholder management UI for configuring Run + PartnerRunData ordering.
// Planned features:
// - Load runs definitions (active only)
// - Select a run to view & reorder partners/orders (drag & drop)
// - Add partner/order entries manually
// - Save ordering & time windows
// - Trigger regeneration for upcoming days

const RunsManagementPage = (props) => {
  const [runs, setRuns] = React.useState([]); // will hold run definitions
  const [selectedRunId, setSelectedRunId] = React.useState(null);
  const [items, setItems] = React.useState([]); // partner/order items for chosen run
  const [saving, setSaving] = React.useState(false);
  const [newRunDescription, setNewRunDescription] = React.useState('');
  // Multi-day bitmask: bits 0..6 represent Sun..Sat
  const [newRunDaysMask, setNewRunDaysMask] = React.useState(1); // default Sunday
  const [partnerType, setPartnerType] = React.useState('C');
  const [selectedCustomer, setSelectedCustomer] = React.useState(null);
  const [selectedSupplier, setSelectedSupplier] = React.useState(null);
  const [orderType, setOrderType] = React.useState('');
  // Removed orderId field per request (order number no longer captured at template level)
  const [windowStart, setWindowStart] = React.useState('');
  const [windowEnd, setWindowEnd] = React.useState('');
  const [extraInfo, setExtraInfo] = React.useState('');
  const [address, setAddress] = React.useState('');
  const [adding, setAdding] = React.useState(false);
  const [customers, setCustomers] = React.useState([]);
  const [suppliers, setSuppliers] = React.useState([]);
  const [draggingId, setDraggingId] = React.useState(null);
  const [orderDirty, setOrderDirty] = React.useState(false);
  const [editingId, setEditingId] = React.useState(null);
  const [addAttempted, setAddAttempted] = React.useState(false);
  const [editAttempted, setEditAttempted] = React.useState(false);
  const [selectedRunEditDescription, setSelectedRunEditDescription] = React.useState('');
  const [selectedRunEditMask, setSelectedRunEditMask] = React.useState(0);
  const [savingRun, setSavingRun] = React.useState(false);
  const [runEditOpenId, setRunEditOpenId] = React.useState(null); // controls visibility of run edit panel
  const [runsLoading, setRunsLoading] = React.useState(true);
  const [itemsLoading, setItemsLoading] = React.useState(false);
  const [itemDialogOpen, setItemDialogOpen] = React.useState(false);
  const isEditing = React.useMemo(()=>!!editingId, [editingId]);
  const [runDialogOpen, setRunDialogOpen] = React.useState(false);
  const [runDialogMode, setRunDialogMode] = React.useState('create'); // 'create' | 'edit'

  const baseUrl = process.env.REACT_APP_BASE_URL;

  // time helpers
  const pad2 = (n) => String(n).padStart(2, '0');
  const toHHMM = (val) => {
    if (!val) return '';
    if (typeof val === 'string') {
      // common cases: 'HH:mm', 'HH:mm:ss', 'YYYY-MM-DDTHH:mm[:ss]Z'
      const m = val.match(/(\d{1,2}):(\d{2})/);
      if (m) {
        const h = Math.min(23, Math.max(0, parseInt(m[1], 10)));
        const mm = Math.min(59, Math.max(0, parseInt(m[2], 10)));
        return `${pad2(h)}:${pad2(mm)}`;
      }
    }
    const d = new Date(val);
    if (!isNaN(d.getTime())) return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
    return '';
  };
  const fromHHMM = (str) => {
    if (!str) return null;
    const m = str.match(/^(\d{1,2}):(\d{2})$/);
    if (!m) return null;
    const h = Math.min(23, Math.max(0, parseInt(m[1], 10)));
    const mm = Math.min(59, Math.max(0, parseInt(m[2], 10)));
    return dayjs().hour(h).minute(mm).second(0).millisecond(0);
  };
  const toHMMa = (str) => {
    const d = fromHHMM(str);
    return d ? d.format('h:mm a') : '';
  };

  // helper to build query string, skipping empty values
  const buildQuery = (params) => {
    return Object.entries(params)
      .filter(([, v]) => v !== undefined && v !== null && v !== '')
      .map(([k, v]) => encodeURIComponent(k) + '=' + encodeURIComponent(v))
      .join('&');
  };

  const reorderItems = (sourceId, targetId) => {
    if (!sourceId || !targetId || sourceId === targetId) return;
    const sorted = [...items].sort((a, b) => a.runOrder - b.runOrder);
    const srcIdx = sorted.findIndex(i => i.id === sourceId);
    const tgtIdx = sorted.findIndex(i => i.id === targetId);
    if (srcIdx < 0 || tgtIdx < 0) return;
    const [moved] = sorted.splice(srcIdx, 1);
    sorted.splice(tgtIdx, 0, moved);
    const reseq = sorted.map((it, idx) => ({ ...it, runOrder: idx + 1 }));
    setItems(reseq);
    setOrderDirty(true);
  };

  const saveOrdering = async () => {
    if (!selectedRunId || !orderDirty) return;
    setSaving(true);
    try {
      const orderedIds = items.sort((a,b)=>a.runOrder-b.runOrder).map(i => i.id).join(',');
      const qs = buildQuery({ RunId: selectedRunId, OrderedIds: orderedIds });
      const rsp = await adalApiFetch(fetch, `${baseUrl}/Logistics/UpdateRunOrder?${qs}`, { method: 'POST' });
      const data = await rsp.json();
      const mapped = (Array.isArray(data) ? data : []).map(d => ({
        id: d.id || d.Id,
        runOrder: d.runOrder || d.RunOrder,
        partnerType: d.partnerType || d.PartnerType,
        partnerId: d.partnerId || d.PartnerId,
        orderType: d.orderType || d.OrderType,
        orderId: d.orderId || d.OrderId,
        windowStart: d.windowStart || d.WindowStart,
        windowEnd: d.windowEnd || d.WindowEnd,
        extraInfo: d.extraInfo || d.ExtraInfo,
        address: d.address || d.Address,
      }));
      setItems(mapped);
      setOrderDirty(false);
    } catch (e) { console.error(e); }
    finally { setSaving(false); }
  };
  // normalize helper
  const toRun = (d) => {
    const mask = d.daysOfWeekMask ?? d.DaysOfWeekMask ?? 0;
    // Server also sends Days array; derive if absent
    const daysArr = d.Days || d.days || Array.from({length:7},(_,i)=>i).filter(i => (mask & (1<<i))!==0);
    return {
      id: d.id ?? d.Id ?? d.runId ?? d.RunId,
      description: d.description ?? d.Description,
      daysOfWeekMask: mask,
      days: daysArr,
      isActive: (d.isActive ?? d.IsActive) === true,
      created: d.created ?? d.Created,
    };
  };

  const loadRuns = async () => {
    setRunsLoading(true);
    try {
      const rsp = await adalApiFetch(fetch, baseUrl + '/Logistics/GetRuns');
      const raw = await rsp.json();
      const mapped = Array.isArray(raw) ? raw.map(toRun) : [];
      setRuns(mapped);
      if (mapped.length && !selectedRunId) setSelectedRunId(mapped[0].id);
      // only preload edit fields if the currently open panel corresponds to selected run
      if (mapped.length && selectedRunId && runEditOpenId === selectedRunId) {
        const sel = mapped.find(r => r.id === selectedRunId);
        if (sel) {
          setSelectedRunEditDescription(sel.description || '');
          setSelectedRunEditMask(sel.daysOfWeekMask || 0);
        }
      }
    } catch (e) { console.error(e); }
    finally { setRunsLoading(false); }
  };

  const loadRunItems = async (runId) => {
    if (!runId) return;
    setItemsLoading(true);
    try {
      const rsp = await adalApiFetch(fetch, baseUrl + '/Logistics/GetRunItems?RunId=' + runId);
      const data = await rsp.json();
      setItems(data.map(d => ({
        id: d.id || d.Id,
        runOrder: d.runOrder || d.RunOrder,
        partnerType: d.partnerType || d.PartnerType,
        partnerId: d.partnerId || d.PartnerId,
        orderType: d.orderType || d.OrderType,
        orderId: d.orderId || d.OrderId,
        windowStart: toHHMM(d.windowStart || d.WindowStart),
        windowEnd: toHHMM(d.windowEnd || d.WindowEnd),
        extraInfo: d.extraInfo || d.ExtraInfo,
        address: d.address || d.Address,
      })) );
  setOrderDirty(false);
    } catch (e) { console.error(e); }
    finally { setItemsLoading(false); }
  };

  React.useEffect(() => { loadRuns(); }, []);
  React.useEffect(() => { loadRunItems(selectedRunId); }, [selectedRunId]);
  // When selection changes, close edit panel if selecting a different run than the one being edited.
  React.useEffect(()=>{
    if (!selectedRunId) return;
    if (runEditOpenId !== selectedRunId) {
      // Not editing this newly selected run; do not overwrite edit fields, just ensure panel hidden
      return;
    }
    const run = runs.find(r=>r.id===selectedRunId);
    if (run) {
      setSelectedRunEditDescription(run.description || '');
      setSelectedRunEditMask(run.daysOfWeekMask || 0);
    }
  }, [selectedRunId, runs, runEditOpenId]);
  React.useEffect(() => {
    // Default and hide order type depending on partner type
    if (partnerType === 'C') {
      setOrderType('SO');
    if (!editingId) setSelectedSupplier(null);
    } else if (partnerType === 'S') {
      setOrderType('PO');
    if (!editingId) setSelectedCustomer(null);
    }
  }, [partnerType, editingId]);
  // Clear validation attempts when selection changes
  React.useEffect(() => {
    if (partnerType === 'C') {
      if (selectedCustomer?.id) { setAddAttempted(false); setEditAttempted(false); }
    } else if (partnerType === 'S') {
      if (selectedSupplier?.id) { setAddAttempted(false); setEditAttempted(false); }
    }
  }, [partnerType, selectedCustomer, selectedSupplier]);
  // Load customer and supplier lists for display mapping
  React.useEffect(() => {
  getAPIData('/Customers/Get', setCustomers);
  getAPIData('/farmerssuppliers', (data) => setSuppliers(data?.farmers || []));
  }, []);
  const customersById = React.useMemo(() => {
    const map = {};
    customers.forEach(c => { if (c?.id != null) map[c.id] = c; });
    return map;
  }, [customers]);
  const suppliersById = React.useMemo(() => {
    const map = {};
    suppliers.forEach(s => { if (s?.id != null) map[s.id] = s; });
    return map;
  }, [suppliers]);

  const createRun = async () => {
    if (!newRunDescription || !newRunDaysMask) return;
    try {
      const qs = buildQuery({ Description: newRunDescription, DaysOfWeekMask: newRunDaysMask });
      const rsp = await adalApiFetch(
        fetch,
        `${baseUrl}/Logistics/CreateRun?${qs}`,
        { method: 'POST' }
      );
      const data = await rsp.json();
      const newRun = toRun(data);
      setRuns(r => [...r, newRun]);
      if (!selectedRunId) setSelectedRunId(newRun.id);
      setNewRunDescription('');
      setNewRunDaysMask(1); // reset to Sunday
    } catch (e) { console.error(e); }
  };

  const toggleDay = (bit) => {
    setNewRunDaysMask(m => {
      const next = (m ^ (1 << bit));
      return next === 0 ? m : next; // prevent clearing all days (must have >=1)
    });
  };
  const toggleEditRunDay = (bit) => {
    setSelectedRunEditMask(m => {
      const next = (m ^ (1 << bit));
      return next === 0 ? m : next; // prevent clearing all days
    });
  };

  const dayLabelShort = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
  const formatRunDays = (run) => {
    if (!run?.days || run.days.length===0) return '';
    if (run.days.length === 7) return 'Daily';
    return run.days.map(d => dayLabelShort[d]).join(', ');
  };

  const addPartner = async () => {
    const chosenPartnerId = partnerType === 'C' ? (selectedCustomer?.id) : (selectedSupplier?.id);
    if (!selectedRunId) return;
    if (!chosenPartnerId) {
      setAddAttempted(true);
      await MySwal.fire({
        icon: 'error',
        title: 'Partner required',
        text: `Please select a ${partnerType === 'C' ? 'customer' : 'supplier'} before adding.`,
      });
      return;
    }
    setAdding(true);
    try {
      const qs = buildQuery({
        RunId: selectedRunId,
        PartnerType: partnerType,
        PartnerId: chosenPartnerId,
        OrderType: orderType,
  // OrderId removed
        WindowStart: windowStart,
        WindowEnd: windowEnd,
        ExtraInfo: extraInfo,
        Address: address,
      });
      const rsp = await adalApiFetch(
        fetch,
        `${baseUrl}/Logistics/AddRunItem?${qs}`,
        { method: 'POST' }
      );
      const data = await rsp.json();
      setItems(it => [...it, {
        id: data.id || data.Id,
        runOrder: data.runOrder || data.RunOrder,
        partnerType: data.partnerType || data.PartnerType,
        partnerId: data.partnerId || data.PartnerId,
        orderType: data.orderType || data.OrderType,
        orderId: data.orderId || data.OrderId,
        windowStart: data.windowStart || data.WindowStart,
        windowEnd: data.windowEnd || data.WindowEnd,
        extraInfo: data.extraInfo || data.ExtraInfo,
        address: data.address || data.Address,
      }]);
      // reset partial form (keep partner type)
  setSelectedCustomer(null); setSelectedSupplier(null); setWindowStart(''); setWindowEnd(''); setExtraInfo(''); setAddress('');
  setAddAttempted(false);
      setItemDialogOpen(false);
    } catch (e) { console.error(e); }
    finally { setAdding(false); }
  };

  const beginEdit = (it) => {
    setEditingId(it.id);
  setEditAttempted(false);
    setPartnerType(it.partnerType);
    if (it.partnerType === 'C') {
      const c = customersById[it.partnerId] || { id: it.partnerId, name: String(it.partnerId) };
      setSelectedCustomer(c);
      setSelectedSupplier(null);
    } else {
      const s = suppliersById[it.partnerId] || { id: it.partnerId, name: String(it.partnerId) };
      setSelectedSupplier(s);
      setSelectedCustomer(null);
    }
  setOrderType(it.orderType || (it.partnerType === 'C' ? 'SO' : 'PO'));
  // orderId removed
  setWindowStart(toHHMM(it.windowStart) || '');
  setWindowEnd(toHHMM(it.windowEnd) || '');
    setExtraInfo(it.extraInfo || '');
    setAddress(it.address || '');
    setItemDialogOpen(true);
  };

  const cancelEdit = () => {
    setEditingId(null);
  setSelectedCustomer(null);
  setSelectedSupplier(null);
  setWindowStart(''); setWindowEnd(''); setExtraInfo(''); setAddress('');
  setEditAttempted(false);
  setItemDialogOpen(false);
  };

  const saveEdit = async () => {
    if (!editingId) return;
    const chosenPartnerId = partnerType === 'C' ? (selectedCustomer?.id) : (selectedSupplier?.id);
    if (!chosenPartnerId) {
      setEditAttempted(true);
      await MySwal.fire({
        icon: 'error',
        title: 'Partner required',
        text: `Please select a ${partnerType === 'C' ? 'customer' : 'supplier'} before saving.`,
      });
      return;
    }
    setSaving(true);
    try {
      const qs = buildQuery({
        Id: editingId,
        PartnerType: partnerType,
        PartnerId: chosenPartnerId,
        OrderType: orderType,
  // OrderId removed
        WindowStart: windowStart,
        WindowEnd: windowEnd,
        ExtraInfo: extraInfo,
        Address: address,
      });
      const rsp = await adalApiFetch(
        fetch,
        `${baseUrl}/Logistics/UpdateRunItem?${qs}`,
        { method: 'POST' }
      );
      const d = await rsp.json();
      const updated = {
        id: d.id || d.Id,
        runOrder: d.runOrder || d.RunOrder,
        partnerType: d.partnerType || d.PartnerType,
        partnerId: d.partnerId || d.PartnerId,
        orderType: d.orderType || d.OrderType,
  // orderId removed
        windowStart: d.windowStart || d.WindowStart,
        windowEnd: d.windowEnd || d.WindowEnd,
        extraInfo: d.extraInfo || d.ExtraInfo,
        address: d.address || d.Address,
      };
      setItems(arr => arr.map(x => x.id === updated.id ? { ...x, ...updated } : x));
      cancelEdit();
  setEditAttempted(false);
    } catch (e) { console.error(e); }
    finally { setSaving(false); }
  };

  const deleteItem = async (id) => {
    if (!id) return;
    const result = await MySwal.fire({
      title: 'Delete this run item?',
      text: 'This will remove the item from the run.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Delete',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#d33',
      focusCancel: true,
    });
    if (!result.isConfirmed) return;
    setSaving(true);
    try {
  const qs = buildQuery({ Id: id });
      const rsp = await adalApiFetch(
        fetch,
        `${baseUrl}/Logistics/DeleteRunItem?${qs}`,
        { method: 'POST' }
      );
      await rsp.json().catch(()=>{});
      if (editingId === id) cancelEdit();
      // reload to get resequenced order
      await loadRunItems(selectedRunId);
    } catch (e) { console.error(e); }
    finally { setSaving(false); }
  };

  const updateRun = async () => {
    if (!selectedRunId) return;
    if (!selectedRunEditDescription || !selectedRunEditMask) return; // mask cannot be zero
    setSavingRun(true);
    try {
      const qs = buildQuery({ Id: selectedRunId, Description: selectedRunEditDescription, DaysOfWeekMask: selectedRunEditMask });
      const rsp = await adalApiFetch(fetch, `${baseUrl}/Logistics/UpdateRun?${qs}`, { method: 'POST' });
      const data = await rsp.json();
      const updated = toRun(data);
      setRuns(arr => arr.map(r => r.id === updated.id ? updated : r));
      await MySwal.fire({ icon: 'success', title: 'Run updated', timer: 1200, showConfirmButton: false });
      // close panel after successful save
      setRunEditOpenId(null);
    } catch (e) { console.error(e); }
    finally { setSavingRun(false); }
  };
  const deactivateRun = async () => {
    if (!selectedRunId) return;
    const run = runs.find(r=>r.id===selectedRunId);
    if (!run || !run.isActive) return;
    const result = await MySwal.fire({
      title: 'Deactivate this run?',
      text: 'It will no longer generate daily items until reactivated.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Deactivate',
      confirmButtonColor: '#d33'
    });
    if (!result.isConfirmed) return;
    setSavingRun(true);
    try {
      const qs = buildQuery({ Id: selectedRunId });
      await adalApiFetch(fetch, `${baseUrl}/Logistics/DeleteRun?${qs}`, { method: 'POST' });
      setRuns(arr => arr.map(r => r.id === selectedRunId ? { ...r, isActive:false } : r));
      await MySwal.fire({ icon: 'success', title: 'Run deactivated', timer: 1200, showConfirmButton: false });
    } catch (e) { console.error(e); }
    finally { setSavingRun(false); }
  };
  const reactivateRun = async () => {
    if (!selectedRunId) return;
    const run = runs.find(r=>r.id===selectedRunId);
    if (!run || run.isActive) return;
    setSavingRun(true);
    try {
      const qs = buildQuery({ Id: selectedRunId, Reactivate: true });
      const rsp = await adalApiFetch(fetch, `${baseUrl}/Logistics/UpdateRun?${qs}`, { method: 'POST' });
      const data = await rsp.json();
      const updated = toRun(data);
      setRuns(arr => arr.map(r => r.id === updated.id ? updated : r));
      await MySwal.fire({ icon: 'success', title: 'Run reactivated', timer: 1200, showConfirmButton: false });
    } catch (e) { console.error(e); }
    finally { setSavingRun(false); }
  };

  const openRunEdit = (run) => {
    // ensure selection matches target run
    setSelectedRunId(run.id);
    setSelectedRunEditDescription(run.description || '');
    setSelectedRunEditMask(run.daysOfWeekMask || 0);
    setRunEditOpenId(run.id);
    setRunDialogMode('edit');
    setRunDialogOpen(true);
  };

  const cancelRunEdit = () => {
    setRunEditOpenId(null);
    setRunDialogOpen(false);
  };

  if (runsLoading) {
    return (
      <Stack alignItems="center" justifyContent="center" sx={{ height: '60vh' }}>
        <CircularProgress />
      </Stack>
    );
  }

  return (
    <React.Fragment>
      <Grid container spacing={2} mb={2}>
        <Grid item xs={12} md={4}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb:1 }}>
            <Typography variant="h6" sx={{ mb:0 }}>Runs</Typography>
            <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={()=>{ setRunDialogMode('create'); setNewRunDescription(''); setNewRunDaysMask(1); setRunDialogOpen(true); }}>New</Button>
          </Stack>
          <Paper variant="outlined" sx={{ maxHeight: 430, overflow: 'auto' }}>
            <List dense>
              {runs.length === 0 && <ListItem><ListItemText primary="No runs defined yet." /></ListItem>}
              {runs.map(r => (
                <ListItem key={r.id} button selected={r.id===selectedRunId} onClick={()=>{setSelectedRunId(r.id); setItems([]);}} secondaryAction={
                  <IconButton edge="end" size="small" aria-label="edit run" onClick={(e)=>{ e.stopPropagation(); openRunEdit(r); }}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                }>
                  <ListItemText primary={r.description} secondary={`${formatRunDays(r)}${r.isActive ? '' : ' (Inactive)'}`} />
                </ListItem>
              ))}
            </List>
          </Paper>
        </Grid>
        <Grid item xs={12} md={8}>
          <Typography variant="h6">Run Items</Typography>
          {selectedRunId ? (
            <Paper variant="outlined" sx={{ p:2, position:'relative', minHeight: itemsLoading ? 200 : 'auto' }}>
              {itemsLoading ? (
                <Stack alignItems="center" justifyContent="center" sx={{ height: 160 }}>
                  <CircularProgress size={32} />
                </Stack>
              ) : (
                <>
              <Stack direction="row" spacing={1} mb={2}>
                <Button variant="contained" size="small" onClick={()=>{ setEditingId(null); setAddAttempted(false); setPartnerType('C'); setSelectedCustomer(null); setSelectedSupplier(null); setWindowStart(''); setWindowEnd(''); setExtraInfo(''); setAddress(''); setItemDialogOpen(true); }}>Add Order Item</Button>
              </Stack>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Drag to reorder. Time windows optional. Select time (AM/PM).
              </Typography>
               {items.length === 0 && <Typography variant="body2">No items for this run yet.</Typography>}
               {items.sort((a,b)=>a.runOrder-b.runOrder).map(it => {
                const name = it.partnerType === 'C'
                  ? (customersById[it.partnerId]?.name || it.partnerId)
                  : (suppliersById[it.partnerId]?.name || it.partnerId);
                const ws = toHMMa(it.windowStart);
                const we = toHMMa(it.windowEnd);
                // Derive customer ShipVia (only for customer partner type). Supports different casings.
                const customerShipVia = it.partnerType === 'C'
                  ? (customersById[it.partnerId]?.shipVia || customersById[it.partnerId]?.ShipVia || '')
                  : '';
                const windowStr = ws && we
                  ? `${ws}-${we}`
                  : (ws ? `>${ws}` : (we ? `<${we}` : ''));
                return (
                  <Paper
                    key={it.id}
                    variant="outlined"
                    sx={{
                      p: 1.1,
                      mb: 0.85,
                      cursor: 'grab',
                      opacity: draggingId === it.id ? 0.6 : 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      backgroundColor: it.partnerType === 'C' ? '#e3f2fd' : '#e8f5e9', // pale blue for customers, pale green for suppliers
                      borderLeft: '4px solid',
                      borderLeftColor: it.partnerType === 'C' ? '#64b5f6' : '#81c784',
                    }}
                    draggable
                    onDragStart={() => setDraggingId(it.id)}
                    onDragOver={(e) => { e.preventDefault(); }}
                    onDrop={() => { reorderItems(draggingId, it.id); setDraggingId(null); }}
                    onDragEnd={() => setDraggingId(null)}
                  >
                    <div>
                      <strong>{it.runOrder}. {name}</strong>  {it.orderType ? `(${it.orderType})` : ''} {customerShipVia && <span> | {customerShipVia}</span>} {windowStr && ` | ${windowStr}`}
                      {it.extraInfo && <span> | {it.extraInfo}</span>} {it.address && <span> | {it.address}</span>}
                    </div>
                    <div onClick={(e)=>e.stopPropagation()} onMouseDown={(e)=>e.stopPropagation()}>
                      <IconButton size="small" aria-label="edit" onClick={()=>beginEdit(it)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" aria-label="delete" onClick={()=>deleteItem(it.id)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </div>
                  </Paper>
                );
              })}
               <Button sx={{ mt:2 }} variant="contained" onClick={saveOrdering} disabled={saving || !orderDirty}>Save Ordering</Button>
                </>
              )}
            </Paper>
          ) : (
            <Typography variant="body2" color="text.secondary">Select a run to manage its items.</Typography>
          )}
        </Grid>
      </Grid>
      <Dialog open={itemDialogOpen} onClose={cancelEdit} maxWidth="md" fullWidth>
        <DialogTitle sx={{ pb:0.5 }}>{isEditing ? 'Edit Run Item' : 'Add Run Item'}</DialogTitle>
        <DialogContent dividers sx={{ pt:1.5 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4} md={3}>
              <FormControl size="small" fullWidth>
                <InputLabel>Type</InputLabel>
                <Select label="Type" value={partnerType} onChange={e=>setPartnerType(e.target.value)}>
                  <MenuItem value="C">Customer</MenuItem>
                  <MenuItem value="S">Supplier</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={8} md={5}>
              {partnerType === 'C' ? (
                <CustomerSelect state={selectedCustomer} setState={setSelectedCustomer} />
              ) : (
                <SupplierSelect state={selectedSupplier} setState={setSelectedSupplier} />
              )}
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <MobileTimePicker
                  ampm
                  minutesStep={5}
                  label="Window Start"
                  value={fromHHMM(windowStart)}
                  onChange={(v)=> setWindowStart(v ? toHHMM(v.toDate()) : '')}
                  format="h:mm a"
                  slotProps={{ textField: { size: 'small', fullWidth:true } }}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <MobileTimePicker
                  ampm
                  minutesStep={5}
                  label="Window End"
                  value={fromHHMM(windowEnd)}
                  onChange={(v)=> setWindowEnd(v ? toHHMM(v.toDate()) : '')}
                  format="h:mm a"
                  slotProps={{ textField: { size: 'small', fullWidth:true } }}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12} sm={6} md={6}>
              <TextField size="small" label="Address Override" value={address} onChange={e=>setAddress(e.target.value)} fullWidth />
            </Grid>
            <Grid item xs={12} sm={6} md={6}>
              <TextField size="small" label="Comments" value={extraInfo} onChange={e=>setExtraInfo(e.target.value)} fullWidth />
            </Grid>
            {(((isEditing ? editAttempted : addAttempted)) && !(partnerType === 'C' ? selectedCustomer?.id : selectedSupplier?.id)) && (
              <Grid item xs={12}>
                <Typography variant="caption" color="error">
                  Please select a {partnerType === 'C' ? 'customer' : 'supplier'}.
                </Typography>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          {isEditing ? (
            <Button variant="contained" onClick={saveEdit} disabled={saving}>Save</Button>
          ) : (
            <Button variant="contained" onClick={addPartner} disabled={adding}>Add</Button>
          )}
          <Button variant="text" onClick={cancelEdit} disabled={saving || adding}>Cancel</Button>
        </DialogActions>
      </Dialog>
      <Dialog open={runDialogOpen} onClose={cancelRunEdit} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ pb:0.5 }}>{runDialogMode === 'edit' ? 'Edit Run' : 'Create Run'}</DialogTitle>
        <DialogContent dividers sx={{ pt:1.5 }}>
          {runDialogMode === 'edit' ? (
            <>
              <TextField size="small" fullWidth label="Description" value={selectedRunEditDescription} onChange={e=>setSelectedRunEditDescription(e.target.value)} sx={{ mb:2 }} />
              <Typography variant="caption" sx={{ display:'block', mb:0.5 }}>Days</Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb:1 }}>
                {dayLabelShort.map((lbl, idx) => {
                  const active = (selectedRunEditMask & (1<<idx)) !== 0;
                  return (
                    <Button key={idx} size="small" variant={active? 'contained':'outlined'} onClick={()=>toggleEditRunDay(idx)} sx={{minWidth:54}}>
                      {lbl}
                    </Button>
                  );
                })}
              </Stack>
            </>
          ) : (
            <>
              <TextField size="small" fullWidth label="Description" value={newRunDescription} onChange={e=>setNewRunDescription(e.target.value)} sx={{ mb:2 }} />
              <Typography variant="caption" sx={{ display:'block', mb:0.5 }}>Days</Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb:1 }}>
                {dayLabelShort.map((lbl, idx) => {
                  const active = (newRunDaysMask & (1<<idx)) !== 0;
                  return (
                    <Button key={idx} size="small" variant={active? 'contained':'outlined'} onClick={()=>toggleDay(idx)} sx={{minWidth:54}}>
                      {lbl}
                    </Button>
                  );
                })}
              </Stack>
            </>
          )}
        </DialogContent>
        <DialogActions>
          {runDialogMode === 'edit' ? (
            <>
              <Button variant="contained" disabled={!selectedRunEditDescription || !selectedRunEditMask || savingRun} onClick={async ()=>{ await updateRun(); setRunDialogOpen(false); }}>Save</Button>
              {runs.find(r=>r.id===selectedRunId)?.isActive ? (
                <Button variant="outlined" color="error" disabled={savingRun} onClick={async ()=>{ await deactivateRun(); setRunDialogOpen(false); }}>Deactivate</Button>
              ) : (
                <Button variant="outlined" color="success" disabled={savingRun} onClick={async ()=>{ await reactivateRun(); setRunDialogOpen(false); }}>Reactivate</Button>
              )}
              <Button variant="text" disabled={savingRun} onClick={cancelRunEdit}>Cancel</Button>
            </>
          ) : (
            <>
              <Button variant="contained" disabled={!newRunDescription} onClick={async ()=>{ await createRun(); setRunDialogOpen(false); }}>Create</Button>
              <Button variant="text" onClick={()=>{ setRunDialogOpen(false); }}>Cancel</Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </React.Fragment>
  );
};

export default RunsManagementPage;
